import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Phone, ChevronRight, ChevronDown, MapPin, Mail, Clock } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const navLinks = [
  { label: "Anasayfa", href: "/" },
  {
    label: "Hakkımızda", href: "/hakkimizda/kurumsal",
    children: [
      { label: "Kurumsal", href: "/hakkimizda/kurumsal" },
      { label: "Misyonumuz", href: "/hakkimizda/misyon" },
      { label: "Vizyonumuz", href: "/hakkimizda/vizyon" },
    ],
  },
  {
    label: "Projeler", href: "/projeler",
    children: [
      { label: "Devam Eden Projeler", href: "/projeler?tab=ongoing" },
      { label: "Tamamlanan Projeler", href: "/projeler?tab=completed" },
    ],
  },
  { label: "İletişim", href: "/iletisim" },
  { label: "Parsel Sorgula", href: "/parsel-sorgulama" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [settings, setSettings] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpenDropdown(null);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/settings`).then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const go = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    setOpenDropdown(null);
    setMobileExpanded(null);
    navigate(href);
  };

  const showSolid = scrolled || !isHome;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 navbar transition-all duration-500 ${showSolid ? "navbar-scrolled" : "navbar-top"}`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between h-20">
        {/* Logo */}
        <a href="/" data-testid="logo" className="flex items-baseline select-none logo-hover group" onClick={(e) => go(e, "/")}>
          <span className="flex" style={{ fontFamily: "Outfit" }}>
            {"BCT".split("").map((letter, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: -40, rotateX: 90 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.12, type: "spring", stiffness: 200, damping: 15 }}
                className="text-2xl font-extrabold tracking-tighter inline-block logo-letter-orange"
                style={{ color: "#FF5A00" }}
              >
                {letter}
              </motion.span>
            ))}
          </span>
          <motion.span
            initial={{ opacity: 0, x: -16, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.55, type: "spring", stiffness: 180, damping: 14 }}
            className={`text-2xl font-extrabold tracking-tighter logo-letter-yapi ${showSolid ? "text-[#0C0C0C]" : "text-white"} transition-colors duration-500`}
            style={{ fontFamily: "Outfit" }}
          >
            YAPI
          </motion.span>
        </a>

        {/* Desktop */}
        <div className="hidden lg:flex items-center gap-8" ref={dropdownRef}>
          {navLinks.map((l) => (
            <div key={l.label} className="relative">
              {l.children ? (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === l.label ? null : l.label); }}
                    className={`nav-link-item flex items-center gap-1 ${showSolid ? "text-[#3A3A3A] hover:text-[#0C0C0C]" : "text-white/80 hover:text-white"} transition-colors duration-500`}
                    style={{ fontFamily: "Outfit" }}
                    data-testid={`nav-${l.label.toLowerCase().replace(/ı/g,"i")}`}
                  >
                    {l.label}
                    <ChevronDown size={13} className={`transition-transform duration-200 ${openDropdown === l.label ? "rotate-180" : ""}`} />
                  </button>
                  {openDropdown === l.label && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[220px] z-50">
                      {l.children.map((c) => (
                        <a key={c.href} href={c.href} onClick={(e) => go(e, c.href)}
                          className="block px-4 py-2.5 text-sm text-gray-600 hover:text-[#FF5A00] hover:bg-orange-50/50 transition-colors font-medium"
                          style={{ fontFamily: "Outfit" }}
                          data-testid={`dropdown-${c.label.toLowerCase().replace(/ /g,"-").replace(/ı/g,"i").replace(/ö/g,"o").replace(/ü/g,"u")}`}
                        >
                          {c.label}
                        </a>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <a href={l.href} data-testid={`nav-${l.label.toLowerCase().replace(/ı/g,"i")}`} onClick={(e) => go(e, l.href)}
                  className={`nav-link-item ${showSolid ? "text-[#3A3A3A] hover:text-[#0C0C0C]" : "text-white/80 hover:text-white"} transition-colors duration-500`}
                  style={{ fontFamily: "Outfit" }}
                >
                  {l.label}
                </a>
              )}
            </div>
          ))}
          <a href="tel:+905454814275" data-testid="nav-phone" className="btn-modern btn-filled text-xs py-3 px-6">
            <Phone size={14} /> Bizi Arayın
          </a>
        </div>

        {/* Mobile Toggle */}
        <button data-testid="mobile-menu-toggle" className="lg:hidden p-2 z-[1002] relative" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X size={24} className="text-[#0C0C0C]" /> : <Menu size={24} className={showSolid ? "text-[#0C0C0C]" : "text-white"} />}
        </button>
      </div>

      {/* Mobile Full Screen Menu */}
      <div className={`fixed inset-0 bg-white z-[1000] lg:hidden transition-transform duration-300 ease-out ${mobileOpen ? "translate-x-0" : "translate-x-full"}`} data-testid="mobile-menu">
        {/* Header */}
        <div className="flex items-center justify-between px-5 h-20 border-b border-gray-100">
          <span className="text-2xl font-extrabold tracking-tighter" style={{ fontFamily: "Outfit" }}>
            <span className="text-[#FF5A00]">BCT</span><span className="text-[#0C0C0C]"> YAPI</span>
          </span>
          <button onClick={() => setMobileOpen(false)} className="p-2" data-testid="mobile-menu-close"><X size={24} className="text-[#0C0C0C]" /></button>
        </div>

        {/* Menu Items + Contact Info */}
        <div className="overflow-y-auto h-[calc(100vh-80px)] bg-white">
          <div className="px-6 py-4 bg-white">
            {navLinks.map((l) => (
              <div key={l.label}>
                {l.children ? (
                  <>
                    <button onClick={() => setMobileExpanded(mobileExpanded === l.label ? null : l.label)}
                      className="flex items-center justify-between w-full py-4 text-lg font-semibold text-[#0C0C0C] border-b border-gray-100" style={{ fontFamily: "Outfit" }} data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
                      {l.label}
                      <ChevronDown size={18} className={`text-gray-400 transition-transform duration-200 ${mobileExpanded === l.label ? "rotate-180" : ""}`} />
                    </button>
                    {mobileExpanded === l.label && (
                      <div className="pl-4 bg-gray-50 rounded-lg my-1">
                        {l.children.map((c) => (
                          <a key={c.href} href={c.href} onClick={(e) => go(e, c.href)}
                            className="block py-3 px-3 text-sm text-gray-600 hover:text-[#FF5A00] font-medium border-b border-gray-100 last:border-0" style={{ fontFamily: "Outfit" }}>
                            {c.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <a href={l.href} onClick={(e) => go(e, l.href)}
                    className="flex items-center justify-between py-4 text-lg font-semibold text-[#0C0C0C] border-b border-gray-100" style={{ fontFamily: "Outfit" }} data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
                    {l.label}
                    <ChevronRight size={18} className="text-gray-400" />
                  </a>
                )}
              </div>
            ))}

            {/* CTA Button */}
            <a href="tel:+905454814275" className="btn-modern btn-filled justify-center mt-6 text-sm w-full" data-testid="mobile-call-btn">
              <Phone size={15} /> Bizi Arayın
            </a>
          </div>

          {/* Contact & Corporate Info */}
          <div className="px-6 py-6 mt-2 bg-gray-50 border-t border-gray-100">
            <p className="text-xs font-bold text-[#FF5A00] uppercase tracking-wider mb-4" style={{ fontFamily: "Outfit" }}>İletişim Bilgileri</p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin size={16} className="text-[#FF5A00] mt-0.5 flex-shrink-0" />
                <p className="text-sm text-gray-600 leading-relaxed">{settings?.address || "Gültepe Mh. Eflatun Cd. ESF İşhanı No:26/210 Batman"}</p>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={16} className="text-[#FF5A00] flex-shrink-0" />
                <a href={`tel:${settings?.phone || "05454814275"}`} className="text-sm text-gray-600 hover:text-[#FF5A00] transition-colors">{settings?.phone || "0545 481 42 75"}</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-[#FF5A00] flex-shrink-0" />
                <a href={`mailto:${settings?.email || "info@bctyapi.com"}`} className="text-sm text-gray-600 hover:text-[#FF5A00] transition-colors">{settings?.email || "info@bctyapi.com"}</a>
              </div>
              <div className="flex items-center gap-3">
                <Clock size={16} className="text-[#FF5A00] flex-shrink-0" />
                <p className="text-sm text-gray-600">{settings?.working_hours || "Pzt - Cuma: 09:00 - 18:00"}</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-5" style={{ fontFamily: "Outfit" }}>{settings?.company_name || "Bct Yapi İnşaat Mühendislik San. ve Tic. Ltd. Şti."}</p>
          </div>
        </div>
      </div>
    </nav>
  );
}
